import { getSelectorAttribute, selectPlanTemplateDialogSelectors } from '@vy/app-selectors';
import React, { useCallback, useState } from 'react';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDebounce } from '../../../../../hooks/use-debounce';
import { SelectPlanTemplateDialogContent } from './components/SelectPlanTemplateDialogContent';
import { useSelectPlanTemplateDialogActions } from './hooks/use-select-plan-template-dialog-actions';
import { useSelectPlanTemplateDialogData } from './hooks/use-select-plan-template-dialog-data';

type SelectPlanTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const SelectPlanTemplateDialog: React.FC<SelectPlanTemplateDialogProps> = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const debouncedSearch = useDebounce(searchValue);

  const { templates, itemsCount, isLoading } = useSelectPlanTemplateDialogData({
    search: debouncedSearch,
    page,
    pageSize,
  });

  const { goToCreatePlanWithClone } = useSelectPlanTemplateDialogActions();

  const isNextDisabled = selectedPlanId === null;

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setSelectedPlanId(null);
    setPage(0);
  }, []);

  const handleClose = useCallback(() => {
    setSearchValue('');
    setSelectedPlanId(null);
    setPage(0);
    setPageSize(10);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (selectedPlanId === null) {
      return;
    }

    goToCreatePlanWithClone(selectedPlanId);

    handleClose();
  }, [selectedPlanId, goToCreatePlanWithClone, handleClose]);

  return (
    <VyDialog
      open={isOpen}
      onClose={handleClose}
      label="Select a plan template"
      labelDescription="Choose a template to use as a base for your plan"
      ctaText="Next"
      cancelButtonText="Cancel"
      onSubmit={handleNext}
      ctaDisabled={isNextDisabled}
      maxWidth="lg"
      sx={{
        '& .MuiDialogContent-root': {
          paddingBottom: '0 !important',
        },
      }}
      cancelButtonProps={{
        sx: {
          padding: '8px 22px',
          borderRadius: '4px',
          backgroundColor: 'rgba(13, 27, 39, 0.08)',
        },
        ...getSelectorAttribute(selectPlanTemplateDialogSelectors.cancelButton),
      }}
      ctaButtonProps={{
        sx: {
          fontSize: '16px',
          padding: '8px 66px',
          borderRadius: '4px',
          fontWeight: 500,
          color: '#FFF',
          backgroundColor: '#0D0D48',
          '&:hover': {
            backgroundColor: '#0D0D48',
          },
        },
        ...getSelectorAttribute(selectPlanTemplateDialogSelectors.nextButton),
      }}
      {...getSelectorAttribute(selectPlanTemplateDialogSelectors.dialog)}
    >
      <SelectPlanTemplateDialogContent
        templates={templates}
        itemsCount={itemsCount}
        isLoading={isLoading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        selectedPlanId={selectedPlanId}
        onSelectionChange={setSelectedPlanId}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </VyDialog>
  );
};
